import { useDrag } from '@use-gesture/react'
import React, { useCallback, useEffect, useState } from 'react'
import { useGestureContainer } from './context'
import './styles.css'

// interface ResizeOverlayProps {
//   mode: 'scale' | 'resize'
//   zoom: number
//   value: [number, number]
//   onResize: (updated: [number, number]) => void
//   constrainAspectRatio?: boolean
// }

export type ResizeFunction = Parameters<typeof useDrag<React.PointerEvent>>[0]

export const useOnScale = (
  value: [number, number],
  minValue: [number, number] | undefined,
  maxValue: [number, number] | undefined,
  constrainAspectRatio: boolean,
  zoom: number,
  onDragStart: () => void,
  onDragEnd: () => void,
  onResize: (updated: [number, number]) => void,
) => {
  const { currHeight, currWidth, updateWidth } = useGestureContainer()
  const dragStartWidth = React.useRef(0)
  const dragStartHeight = React.useRef(0)
  const dragStartScale = React.useRef([0, 0])
  return useCallback<ResizeFunction>(
    ({ movement: [dx, dy], event, first, last }) => {
      event.stopPropagation()
      if (first) {
        dragStartWidth.current = currWidth.current
        dragStartHeight.current = currHeight.current
        dragStartScale.current = [value[0], value[1]]
        onDragStart()
        // Don't process resize on first event to avoid immediate jump
      }
      if (last) {
        onDragEnd()
        updateWidth(currWidth.current)
      }
      requestAnimationFrame(() => {
        if (!dragStartWidth.current || !dragStartHeight.current) return
        const baseWidth = dragStartWidth.current + dx * zoom
        const baseHeight = dragStartHeight.current - dy * zoom

        // Safety checks to prevent division by zero
        const safeDragStartWidth = Math.max(dragStartWidth.current, 0.001)
        const safeDragStartHeight = Math.max(dragStartHeight.current, 0.001)

        const minScale = Math.min(baseWidth / safeDragStartWidth, baseHeight / safeDragStartHeight)

        const newHeight = constrainAspectRatio ? safeDragStartHeight * minScale : baseHeight
        const newWidth = constrainAspectRatio ? safeDragStartWidth * minScale : baseWidth

        const scaleX = (newWidth / safeDragStartWidth) * dragStartScale.current[0]
        const scaleY = (newHeight / safeDragStartHeight) * dragStartScale.current[1]
        // currWidth.current = newWidth
        // currHeight.current = newHeight
        onResize([
          Math.min(maxValue?.[0] ?? 2, Math.max(scaleX, minValue?.[0] ?? 0.5)),
          Math.min(maxValue?.[1] ?? 2, Math.max(scaleY, minValue?.[1] ?? 0.5)),
        ])
      })
    },
    [
      constrainAspectRatio,
      onResize,
      value,
      updateWidth,
      currWidth,
      currHeight,
      zoom,
      onDragStart,
      onDragEnd,
      minValue,
      maxValue,
    ],
  )
}

const clamp = (value: number, min: number | undefined, max: number | undefined) => {
  if (min !== undefined && value < min) return min
  if (max !== undefined && value > max) return max
  return value
}

export const useOnResize = (
  value: [number, number],
  minValue: [number, number] | undefined,
  maxValue: [number, number] | undefined,
  constrainAspectRatio: boolean,
  zoom: number,
  onDragStart: () => void,
  onDragEnd: () => void,
  onResize: (updated: [number, number]) => void,
) => {
  const { currHeight, currWidth, updateWidth } = useGestureContainer()
  const dragStartWidth = React.useRef(0)
  const dragStartHeight = React.useRef(0)
  const dragStartAspectRatio = React.useRef(0)

  const [shiftConstrain, setShiftConstrain] = useState(false)
  const onKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      setShiftConstrain(true)
    }
  }, [])

  const onKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      setShiftConstrain(false)
    }
  }, [])

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [onKeyDown, onKeyUp])

  return useCallback<ResizeFunction>(
    ({ movement: [dx, dy], event, first, last }) => {
      event.stopPropagation()
      if (first) {
        dragStartWidth.current = value[0]
        dragStartHeight.current = value[1]
        dragStartAspectRatio.current = currWidth.current / currHeight.current
        onDragStart()
      }
      if (last) {
        onDragEnd()
        updateWidth(currWidth.current)
      }
      requestAnimationFrame(() => {
        if (!dragStartWidth.current || !dragStartHeight.current) return
        const shouldConstrain = constrainAspectRatio || shiftConstrain
        const baseWidth = dragStartWidth.current + dx * zoom
        const baseHeight = dragStartHeight.current - dy * zoom

        // Safety checks to prevent division by zero
        const safeDragStartWidth = Math.max(dragStartWidth.current, 0.001)
        const safeDragStartHeight = Math.max(dragStartHeight.current, 0.001)

        if (shouldConstrain) {
          const newScale = Math.min(
            baseWidth / safeDragStartWidth,
            baseHeight / safeDragStartHeight,
          )
          currWidth.current = clamp(safeDragStartWidth * newScale, minValue?.[0], maxValue?.[0])
          currHeight.current = clamp(safeDragStartHeight * newScale, minValue?.[1], maxValue?.[1])
          onResize([currWidth.current, currHeight.current])
          return
        } else {
          currWidth.current = clamp(baseWidth, minValue?.[0], maxValue?.[0])
          currHeight.current = clamp(baseHeight, minValue?.[1], maxValue?.[1])
          onResize([currWidth.current, currHeight.current])
        }
      })
    },
    [
      zoom,
      constrainAspectRatio,
      onResize,
      updateWidth,
      currWidth,
      currHeight,
      shiftConstrain,
      value,
      minValue,
      maxValue,
      onDragStart,
      onDragEnd,
    ],
  )
}
